/**
 * Unified driving input: keyboard, gamepad and touch, folded into one KartInput.
 *
 * Each source contributes independently and the strongest wins, so a controller
 * can be picked up mid-race without anything being switched over. Keyboard steer
 * is a hard -1/0/1; stick and touch are analog, which the physics takes as-is
 * (`turnRate = handling * steer`) and which the front wheels visibly follow.
 *
 * The on-screen pad only appears once the device proves it has a touchscreen —
 * either the pointer is coarse at startup, or a touch actually happens — so
 * hybrid laptops are not permanently given a thumb pad they will never use.
 */
import { NO_INPUT, type KartInput } from './types';

/** Below this, a stick is treated as centred. */
const STICK_DEADZONE = 0.16;
/** Drag distance, in CSS pixels, for full lock on each touch-stick axis. */
const TOUCH_LOCK_X = 62;
const TOUCH_LOCK_Y = 58;
/**
 * Deadzones for the touch stick. The vertical one is the larger of the two: a
 * thumb steering hard sideways drifts up or down a little, and that must not
 * read as lifting off or braking.
 */
const TOUCH_DEADZONE_X = 0.08;
const TOUCH_DEADZONE_Y = 0.18;
/** How far the knob travels from the base centre, in CSS pixels. */
const KNOB_TRAVEL = 34;

const STYLE_ID = 'wkr-touch-style';

const STYLE = `
.tc-root {
  position: absolute;
  inset: 0;
  pointer-events: none;
  display: none;
  touch-action: none;
  z-index: 25;
  user-select: none;
  -webkit-user-select: none;
}
.tc-root.tc-on { display: block; }

/* Left half is one big floating-stick zone; the visual only shows while held.
   The stick carries both axes: sideways steers, up and down are gas and brake. */
.tc-steer-zone {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50%;
  height: 68%;
  pointer-events: auto;
}
.tc-stick-base {
  position: absolute;
  width: 132px;
  height: 132px;
  margin: -66px 0 0 -66px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.22);
  background: rgba(10, 14, 26, 0.34);
  backdrop-filter: blur(6px);
  opacity: 0;
  transition: opacity 0.14s ease;
}
.tc-stick-base.tc-held { opacity: 1; }
.tc-stick-knob {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 62px;
  height: 62px;
  margin: -31px 0 0 -31px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.30);
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}

/* Stacked vertically in the bottom-right corner: two buttons, one thumb, and
   nothing competing with the speedo in the middle. */
.tc-pads {
  position: absolute;
  right: calc(16px + env(safe-area-inset-right));
  bottom: calc(16px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}
.tc-btn {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text, #eef1ff);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(10, 14, 26, 0.42);
  backdrop-filter: blur(8px);
  transition: transform 0.06s ease, background 0.1s ease, border-color 0.1s ease;
}
.tc-btn.tc-pressed {
  transform: scale(0.94);
  background: rgba(255, 207, 51, 0.28);
  border-color: var(--accent, #ffcf33);
}
.tc-drift { width: 76px; height: 76px; font-size: 12px; }
.tc-boost { width: 94px; height: 94px; font-size: 13px; }

@media (max-height: 460px) {
  .tc-drift { width: 62px; height: 62px; font-size: 11px; }
  .tc-boost { width: 76px; height: 76px; font-size: 12px; }
  .tc-stick-base { width: 108px; height: 108px; margin: -54px 0 0 -54px; }
}
`;

function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

/** Drop everything inside `zone` and rescale the rest back to the full range. */
function applyDeadzone(value: number, zone: number): number {
  const magnitude = Math.abs(value);
  if (magnitude < zone) return 0;
  return Math.sign(value) * ((magnitude - zone) / (1 - zone));
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Map a thumb drag, in CSS pixels from where the stick was planted, onto the
 * driving axes.
 *
 * Both axes invert, and getting either wrong is invisible until someone tries
 * to drive: `steer` is +1 for LEFT (the keyboard sets `left ? 1 : 0`) while
 * clientX grows rightward, and `throttle` is +1 for forward while clientY grows
 * downward. Exported so the sign convention can be pinned by a test.
 */
export function stickAxes(dxPixels: number, dyPixels: number): { steer: number; throttle: number } {
  const dx = clamp(dxPixels / TOUCH_LOCK_X, -1, 1);
  const dy = clamp(dyPixels / TOUCH_LOCK_Y, -1, 1);
  // Plain negation would hand back -0 at rest, which is a needlessly surprising
  // value for "centred" to compare against.
  const flip = (v: number): number => (v === 0 ? 0 : -v);
  return {
    steer: flip(applyDeadzone(dx, TOUCH_DEADZONE_X)),
    throttle: flip(applyDeadzone(dy, TOUCH_DEADZONE_Y)),
  };
}

/** Keys the driving layer claims; anything else falls through to the page. */
const DRIVE_CODES = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Space', 'ShiftLeft', 'ShiftRight',
]);

export interface InputOptions {
  /** True while a text field has focus, so typing never drives the kart. */
  isTyping(): boolean;
  /** Fired on the mute key. */
  onToggleMute(): void;
}

export class InputManager {
  private readonly keys = new Set<string>();
  private readonly options: InputOptions;

  // Touch state.
  private readonly root: HTMLDivElement;
  private readonly stickBase: HTMLDivElement;
  private readonly stickKnob: HTMLDivElement;
  private touchAvailable: boolean;
  private racing = false;
  private stickPointer: number | null = null;
  private stickOriginX = 0;
  private stickOriginY = 0;
  /** +1 steers left, matching the keyboard convention. */
  private stickSteer = 0;
  /** +1 is full gas, -1 brakes and then reverses. */
  private stickThrottle = 0;
  private touchBoost = false;
  private touchDrift = false;

  /** True once a gamepad has been seen, so the HUD can hint at it if it likes. */
  gamepadConnected = false;

  constructor(host: HTMLElement, options: InputOptions) {
    this.options = options;
    const doc = host.ownerDocument;
    ensureStyle(doc);

    // A coarse primary pointer means a phone or tablet; hybrids get the pad on
    // their first actual touch instead.
    this.touchAvailable =
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

    const root = doc.createElement('div');
    root.className = 'tc-root';
    root.innerHTML = `
      <div class="tc-steer-zone" data-tc="zone">
        <div class="tc-stick-base" data-tc="base"><div class="tc-stick-knob" data-tc="knob"></div></div>
      </div>
      <div class="tc-pads">
        <div class="tc-btn tc-drift" data-tc="drift">Drift</div>
        <div class="tc-btn tc-boost" data-tc="boost">Boost</div>
      </div>
    `;
    host.appendChild(root);
    this.root = root;

    const q = <T extends HTMLElement>(name: string): T => {
      const found = root.querySelector<T>(`[data-tc="${name}"]`);
      if (!found) throw new Error(`Touch control missing: ${name}`);
      return found;
    };
    this.stickBase = q<HTMLDivElement>('base');
    this.stickKnob = q<HTMLDivElement>('knob');

    this.bindStick(q<HTMLDivElement>('zone'));
    this.bindButton(q<HTMLDivElement>('boost'), (down) => { this.touchBoost = down; });
    this.bindButton(q<HTMLDivElement>('drift'), (down) => { this.touchDrift = down; });

    this.bindKeyboard();
    this.bindGamepad();
  }

  // -------------------------------------------------------------------------
  // Sources
  // -------------------------------------------------------------------------

  private bindKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      if (this.options.isTyping()) return;
      if (e.code === 'KeyM' && !e.repeat) {
        this.options.onToggleMute();
        return;
      }
      if (DRIVE_CODES.has(e.code)) {
        this.keys.add(e.code);
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
  }

  private bindGamepad(): void {
    window.addEventListener('gamepadconnected', () => { this.gamepadConnected = true; });
    window.addEventListener('gamepaddisconnected', () => {
      this.gamepadConnected = navigator.getGamepads().some((p) => p !== null);
    });
  }

  /**
   * Floating two-axis stick: the base jumps to wherever the thumb lands, then
   * sideways steers and up/down is gas and brake — one thumb drives the kart.
   *
   * Screen axes and game axes disagree twice over, which is worth spelling out:
   * `steer` is +1 for LEFT (the keyboard sets `left ? 1 : 0`) while clientX
   * grows to the right, and `throttle` is +1 for forward while clientY grows
   * downward. Both therefore negate.
   */
  private bindStick(zone: HTMLElement): void {
    zone.addEventListener('pointerdown', (e) => {
      this.noteTouch(e);
      if (this.stickPointer !== null) return;
      this.stickPointer = e.pointerId;
      this.stickOriginX = e.clientX;
      this.stickOriginY = e.clientY;
      zone.setPointerCapture(e.pointerId);

      const rect = zone.getBoundingClientRect();
      this.stickBase.style.left = `${e.clientX - rect.left}px`;
      this.stickBase.style.top = `${e.clientY - rect.top}px`;
      this.stickBase.classList.add('tc-held');
      e.preventDefault();
    });

    zone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.stickPointer) return;
      const rawX = e.clientX - this.stickOriginX;
      const rawY = e.clientY - this.stickOriginY;
      const axes = stickAxes(rawX, rawY);
      this.stickSteer = axes.steer;
      this.stickThrottle = axes.throttle;

      // The knob tracks the raw thumb, not the deadzoned value, so it never
      // looks stuck while the thumb is clearly moving.
      this.stickKnob.style.transform = `translate(${
        clamp(rawX / TOUCH_LOCK_X, -1, 1) * KNOB_TRAVEL
      }px, ${clamp(rawY / TOUCH_LOCK_Y, -1, 1) * KNOB_TRAVEL}px)`;
      e.preventDefault();
    });

    const release = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return;
      this.stickPointer = null;
      this.stickSteer = 0;
      this.stickThrottle = 0;
      this.stickKnob.style.transform = '';
      this.stickBase.classList.remove('tc-held');
    };
    zone.addEventListener('pointerup', release);
    zone.addEventListener('pointercancel', release);
    zone.addEventListener('lostpointercapture', release);
  }

  private bindButton(el: HTMLElement, set: (down: boolean) => void): void {
    el.addEventListener('pointerdown', (e) => {
      this.noteTouch(e);
      set(true);
      el.classList.add('tc-pressed');
      el.setPointerCapture(e.pointerId);
      e.preventDefault();
    });
    const up = (): void => {
      set(false);
      el.classList.remove('tc-pressed');
    };
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('lostpointercapture', up);
  }

  /** A real touch reveals the pad, even if the device claimed a fine pointer. */
  private noteTouch(e: PointerEvent): void {
    if (e.pointerType !== 'touch' || this.touchAvailable) return;
    this.touchAvailable = true;
    this.applyVisibility();
  }

  // -------------------------------------------------------------------------
  // Output
  // -------------------------------------------------------------------------

  /** Show or hide the on-screen pad as the race starts and ends. */
  setRacing(racing: boolean): void {
    this.racing = racing;
    this.applyVisibility();
  }

  private applyVisibility(): void {
    this.root.classList.toggle('tc-on', this.racing && this.touchAvailable);
  }

  /** Read the first connected gamepad into a partial input, if there is one. */
  private readGamepad(): KartInput | null {
    if (typeof navigator.getGamepads !== 'function') return null;
    for (const pad of navigator.getGamepads()) {
      if (!pad) continue;
      this.gamepadConnected = true;

      // Triggers are analog on a standard mapping; fall back to face buttons.
      const forward = pad.buttons[7]?.value ?? 0;
      const back = pad.buttons[6]?.value ?? 0;
      // Stick right must steer right, and steer is +1 for left, so negate.
      let steer = -applyDeadzone(pad.axes[0] ?? 0, STICK_DEADZONE);
      if (pad.buttons[14]?.pressed) steer = 1; // d-pad left
      if (pad.buttons[15]?.pressed) steer = -1; // d-pad right

      return {
        throttle: clamp(forward - back, -1, 1),
        steer,
        boost: (pad.buttons[0]?.pressed ?? false) || (pad.buttons[5]?.pressed ?? false),
        drift: (pad.buttons[1]?.pressed ?? false) || (pad.buttons[4]?.pressed ?? false),
      };
    }
    return null;
  }

  /**
   * Combine every source. Each axis takes whichever input is pushed furthest, so
   * sources never cancel each other out and switching between them is seamless.
   */
  read(): KartInput {
    if (this.options.isTyping()) return NO_INPUT;

    const forward = this.keys.has('KeyW') || this.keys.has('ArrowUp');
    const back = this.keys.has('KeyS') || this.keys.has('ArrowDown');
    const left = this.keys.has('KeyA') || this.keys.has('ArrowLeft');
    const right = this.keys.has('KeyD') || this.keys.has('ArrowRight');

    let throttle = (forward ? 1 : 0) + (back ? -1 : 0);
    // A/left steers left, D/right steers right (matches the chase-cam view).
    let steer = (left ? 1 : 0) + (right ? -1 : 0);
    let boost = this.keys.has('Space');
    let drift = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');

    const stronger = (a: number, b: number): number => (Math.abs(b) > Math.abs(a) ? b : a);

    const pad = this.readGamepad();
    if (pad) {
      throttle = stronger(throttle, pad.throttle);
      steer = stronger(steer, pad.steer);
      boost = boost || pad.boost;
      drift = drift || pad.drift;
    }

    if (this.touchAvailable) {
      throttle = stronger(throttle, this.stickThrottle);
      steer = stronger(steer, this.stickSteer);
      boost = boost || this.touchBoost;
      drift = drift || this.touchDrift;
    }

    return { throttle, steer, boost, drift };
  }
}
